import React from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { showToast } from "ui/components/feedback/Toast";

type ValidationType = "enum" | "number" | "uuid";

type Props = {
  children: JSX.Element;
  type: ValidationType;
  paramName: string;
  enumType?: Record<string, any>; // Optional, only needed for enum validation
};

export const ValidateRoute = ({
  children,
  type: validationType,
  paramName,
  enumType,
}: Props) => {
  const [searchParams] = useSearchParams();
  const params = useParams();
  const paramValue = searchParams.get(paramName) || params[paramName];

  const validate = (value: string): boolean => {
    switch (validationType) {
      case "uuid":
        return isValidUuid(value);
      case "number":
        return isValidId(value);
      case "enum":
        return enumType ? isValidEnum(value, enumType) : false;
      default:
        return false;
    }
  };

  if (!paramValue || !validate(paramValue)) {
    const enumValues = enumType ? Object.values(enumType).join(", ") : "";
    const message = enumType
      ? `one of [${enumValues}].`
      : `a valid ${validationType}.`;
    const genericErrorMessage = `The URL is wrong. "${paramName}" should be ${message}`;

    showToast(genericErrorMessage, "warning");
    return <Navigate to="/home" replace />;
  }

  return children;
};

const isValidUuid = (id: string) => {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(id);
};

const isValidId = (id: string) => {
  const integerRegex = /^\d+$/;
  return integerRegex.test(id);
};

const isValidEnum = (value: string, enumType: Record<string, unknown>) =>
  value in enumType || Object.values(enumType).includes(value);
